import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, from, of, switchMap, catchError, throwError } from 'rxjs';
import { SecureChannelService, isSecurePath } from '../crypto/secure-channel.service';

/**
 * Wraps request bodies and unwraps response bodies for vault endpoints.
 *
 * **Must be registered last** in `withInterceptors([...])`. Interceptors run left-to-right on the
 * way out and right-to-left on the way back, so last means closest to the network: it encrypts
 * after the tenant and auth interceptors have added their headers, and decrypts before the error
 * interceptor inspects the body. Move it earlier and those interceptors start seeing ciphertext.
 *
 * A 410 means the server no longer holds the session — after a restart, or once it aged out.
 * That is recoverable, so the channel is dropped and the request replayed once against a fresh
 * session. Only once: a second 410 is a real fault, not a stale key.
 */
export const secureInterceptor: HttpInterceptorFn = (req, next) => {
  const channel = inject(SecureChannelService);

  if (!isSecurePath(req.url)) {
    return next(req);
  }

  return send(channel, req, next, true);
};

function send(
  channel: SecureChannelService,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  allowRetry: boolean,
): Observable<HttpEvent<unknown>> {
  const ctx = { nonce: '', timestamp: '', path: '' };

  return from(prepare(channel, req, ctx)).pipe(
    switchMap(sealedRequest =>
      next(sealedRequest).pipe(
        switchMap(event =>
          event instanceof HttpResponse
            ? from(unseal(channel, event, req.method, ctx))
            : of(event),
        ),
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse && error.status === 410 && allowRetry) {
            channel.reset();
            return send(channel, req, next, false);
          }
          return from(unsealError(channel, error, req.method, ctx)).pipe(
            switchMap(decrypted => throwError(() => decrypted)),
          );
        }),
      ),
    ),
  );
}

async function prepare(
  channel: SecureChannelService,
  req: HttpRequest<unknown>,
  ctx: { nonce: string; timestamp: string; path: string },
): Promise<HttpRequest<unknown>> {
  await channel.ensureSession();

  const generated = channel.newEnvelopeContext(req.method, req.url);
  ctx.nonce = generated.nonce;
  ctx.timestamp = generated.timestamp;
  ctx.path = generated.path;

  const headers = {
    'X-Session-Id': channel.getSessionId() ?? '',
    'X-Nonce': ctx.nonce,
    'X-Timestamp': ctx.timestamp,
  };

  // File uploads stay multipart so the bytes stream instead of being buffered and base64'd. The
  // server encrypts the file content before it reaches storage, and the metadata part is sealed
  // by the caller.
  const isMultipart = req.body instanceof FormData;

  if (req.body === null || req.body === undefined || isMultipart) {
    return req.clone({ setHeaders: headers });
  }

  const payload = await channel.sealRequest(req.body, req.method, ctx.path, ctx.nonce, ctx.timestamp);

  return req.clone({ setHeaders: headers, body: { p: payload } });
}

async function unseal(
  channel: SecureChannelService,
  response: HttpResponse<unknown>,
  method: string,
  ctx: { nonce: string; path: string },
): Promise<HttpResponse<unknown>> {
  const payload = extractPayload(response.body);
  if (payload === null) {
    // 204s and other bodyless responses pass through — there is nothing to unseal.
    return response;
  }

  const body = await channel.openResponse(payload, method, ctx.path, ctx.nonce);
  return response.clone({ body });
}

/**
 * Decrypts a sealed error body so downstream handlers see the real message. Middleware-level
 * failures (bad envelope, expired session) answer in plaintext by necessity — those have no `p`
 * and pass through untouched.
 */
async function unsealError(
  channel: SecureChannelService,
  error: unknown,
  method: string,
  ctx: { nonce: string; path: string },
): Promise<unknown> {
  if (!(error instanceof HttpErrorResponse)) {
    return error;
  }

  const payload = extractPayload(error.error);
  if (payload === null) {
    return error;
  }

  try {
    const body = await channel.openResponse(payload, method, ctx.path, ctx.nonce);
    return new HttpErrorResponse({
      error: body,
      headers: error.headers,
      status: error.status,
      statusText: error.statusText,
      url: error.url ?? undefined,
    });
  } catch {
    // Failing to decrypt an error body should not mask the original failure — surface that.
    return error;
  }
}

function extractPayload(body: unknown): string | null {
  if (body && typeof body === 'object' && 'p' in body) {
    const payload = (body as { p: unknown }).p;
    return typeof payload === 'string' ? payload : null;
  }
  return null;
}
